<template>
	<Modal :id="modalId" large>
		<template #title>{{ purchaseOrder.user.full_name }}</template>
		<template #subtitle>{{ dayjs(purchaseOrder.created_at).format(trans('general.dayjs.datetime')) }}</template>
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
			<div class="flex flex-col items-stretch gap-3">
				<Heading>{{ trans('enrollments.purchase_orders.summary') }}</Heading>
				<table class="w-full">
					<thead>
						<tr class="border-b-2 border-gray-200 dark:border-gray-800">
							<th class="px-2 py-1 text-left">{{ trans('enrollments.invoice.description') }}</th>
							<th class="px-2 py-1 text-right">{{ trans('enrollments.invoice.price') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in purchaseOrder.purchase_order_items" class="border-y border-gray-200 dark:border-gray-800">
							<th scope="row" class="px-2 py-1 text-left">
								<i v-if="purchaseOrder.purchase_order_items.length > 1 && !purchaseOrder.is_accepted && !purchaseOrder.is_rejected" class="fa-solid fa-trash mr-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 cursor-pointer" @click.stop="removeItem(item)"/>
								{{ item.payable.title }}
							</th>
							<td class="px-2 py-1 text-right whitespace-nowrap">$ {{ formatNumber(item.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</td>
						</tr>
						<tr v-if="purchaseOrder.purchase_order_items.length === 0" class="border-y border-gray-200 dark:border-gray-800">
							<td colspan="2" class="px-2 py-1 text-left text-gray-400 dark:text-gray-600">{{ trans('general.empty') }}</td>
						</tr>
						<tr class="border-t-2 border-gray-200 dark:border-gray-800 text-teal-500 uppercase">
							<th scope="row" class="px-2 py-1 text-left">{{ trans('enrollments.invoice.total') }}</th>
							<td class="px-2 py-1 text-right font-bold whitespace-nowrap">$ {{ formatNumber(purchaseOrder.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="flex flex-col items-stretch gap-3">
				<Heading>{{ trans('enrollments.purchase_orders.receipts.title') }}</Heading>
				<SubHeading>{{ trans('enrollments.purchase_orders.receipts.description') }}</SubHeading>
				<LabeledObject v-if="(purchaseOrder.is_awaiting || purchaseOrder.is_pending) && purchaseOrder.payment_deadline">
					<template #label>{{ trans('enrollments.purchase_orders.payment_deadline') }}</template>
					<p class="text-red-600 dark:text-red-400">{{ dayjs(purchaseOrder.payment_deadline).format(trans('general.dayjs.datetime')) }}</p>
				</LabeledObject>
				<List>
					<ListItem
						v-for="receipt in purchaseOrder.payment_receipts"
						:key="`payment-receipt-${receipt.id}`"
						:title="trans('enrollments.purchase_orders.receipts.identifier', {id: receipt.id.toString().padStart(6, '0')})"
						:subtitle="dayjs(receipt.created_at).format(trans('general.dayjs.datetime'))"
						class="cursor-pointer"
						@click.stop="selectReceipt(receipt)"
					>
						<template #icon>
							<i class="fa-solid fa-receipt"/>
						</template>
						<i class="fa-solid fa-chevron-right text-gray-400 dark:text-gray-600"/>
					</ListItem>
					<p v-if="purchaseOrder.payment_receipts.length === 0" class="text-gray-400 dark:text-gray-600">{{ trans('general.empty') }}</p>
				</List>
			</div>
			<div v-if="!selectedReceipt" class="p-10 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center gap-8 text-gray-400 dark:text-gray-600">
				<i class="fa-solid fa-file-pdf fa-3x"/>
				<p>{{ trans('enrollments.purchase_orders.receipts.empty') }}</p>
			</div>
			<template v-else>
				<iframe v-if="selectedReceipt.is_pdf" :key="`selected-receipt-${selectedReceipt.id}-pdf`" class="w-full min-h-96" :src="route('events.enrollments.purchase-orders.receipts.show', [event.id, selectedReceipt.id])"/>
				<img v-else :key="`selected-receipt-${selectedReceipt.id}-image`" class="w-full" :src="route('events.enrollments.purchase-orders.receipts.show', [event.id, selectedReceipt.id])" alt>
			</template>
		</div>
		<template v-if="purchaseOrder.is_pending || purchaseOrder.is_awaiting" #footer>
			<SecondaryButton :color="Color.Green" @click.stop="accept">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.accept') }}
			</SecondaryButton>
			<SoftButton :color="Color.Red" @click.stop="reject">
				<i class="fa-solid fa-ban mr-2"/>
				{{ trans('general.reject') }}
			</SoftButton>
		</template>
	</Modal>
	<Card :title="purchaseOrder.user.last_name" :subtitle="purchaseOrder.user.names">
		<template #badges>
			<Badge :color="badgeColor" active>{{ trans(`enrollments.purchase_orders.status.${purchaseOrder.status}`) }}</Badge>
			<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(purchaseOrder.id.toString())">#{{ purchaseOrder.id }}</Badge>
		</template>
		<LabeledObject>
			<template #label>{{ trans('enrollments.purchase_orders.payment_deadline') }}</template>
			<p v-if="purchaseOrder.payment_deadline">{{ dayjs(purchaseOrder.payment_deadline).format(trans('general.dayjs.datetime')) }}</p>
			<p v-else>{{ trans('enrollments.purchase_orders.no_deadline') }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('enrollments.purchase_orders.amount_owed') }}</template>
			<p>$ {{ formatNumber(purchaseOrder.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('enrollments.purchase_orders.items') }}</template>
			<p>{{ purchaseOrder.purchase_order_items.length }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('enrollments.purchase_orders.receipts.uploaded') }}</template>
			<p v-if="purchaseOrder.payment_receipts.length > 0" class="text-green-600 dark:text-green-400">{{ purchaseOrder.payment_receipts.length }}</p>
			<p v-else class="text-red-600 dark:text-red-400">{{ purchaseOrder.payment_receipts.length }}</p>
		</LabeledObject>
		<template v-if="canUpdate || canDelete" #footer>
			<SecondaryButton v-if="canUpdate" :color="Color.Blue" @click.stop="showModal(modalId)">
				<i class="fa-solid fa-eye mr-2"/>
				{{ trans('general.details') }}
			</SecondaryButton>
			<SoftButton v-if="canDelete" :color="Color.Red" @click.stop="destroy">
				<i class="fa-solid fa-trash mr-2"/>
				{{ trans('general.delete') }}
			</SoftButton>
		</template>
	</Card>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import Heading from '@/Components/UI/Typography/Heading.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import SubHeading from '@/Components/UI/Typography/SubHeading.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {defineEmits, defineProps, type Ref, ref, type ComputedRef, computed} from 'vue'
import {getPurchaseOrderStatusColor} from '@/Helpers/Events/Enrollments/purchase-orders'
import {type PurchaseOrder} from '@/Types/Events/Enrollments/PurchaseOrders/PurchaseOrder'
import {type PaymentReceipt} from '@/Types/Events/Enrollments/PurchaseOrders/PaymentReceipt'
import {copyTextToClipboard, formatNumber, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'
import {PurchaseOrderItem} from '@/Types/Events/Enrollments/PurchaseOrders/PurchaseOrderItem'

const props = defineProps<{
	event:Event,
	canUpdate:boolean,
	canDelete:boolean,
	purchaseOrder:PurchaseOrder,
}>()

const emit = defineEmits([
	'delete',
])

const working:Ref<boolean> = ref(false)
const event:Ref<Event> = ref(props.event)
const canUpdate:Ref<boolean> = ref(props.canUpdate)
const canDelete:Ref<boolean> = ref(props.canDelete)
const selectedReceipt:Ref<PaymentReceipt> = ref(null)
const purchaseOrder:Ref<PurchaseOrder> = ref(props.purchaseOrder)
const modalId:Ref<string> = ref(`purchase-order-${purchaseOrder.value.id}-modal`)

const badgeColor:ComputedRef<Color> = computed(():Color => getPurchaseOrderStatusColor(purchaseOrder.value.status))

function selectReceipt(receipt:PaymentReceipt):void{
	selectedReceipt.value = receipt
}

function accept():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.enrollments.purchase-orders.update', [event.value.id, purchaseOrder.value.id]), {
			action: 'accept',
		}).then(({data}:{data:PurchaseOrder}) => {
			showSuccess(trans('general.updated'))
			purchaseOrder.value = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function reject():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.enrollments.purchase-orders.update', [event.value.id, purchaseOrder.value.id]), {
			action: 'reject',
		}).then(({data}:{data:PurchaseOrder}) => {
			showSuccess(trans('general.updated'))
			purchaseOrder.value = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.enrollments.purchase-orders.destroy', [event.value.id, purchaseOrder.value.id])).then(() => {
			showSuccess(trans('general.deleted'))
			emit('delete', purchaseOrder.value.id)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function removeItem(item:PurchaseOrderItem):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.enrollments.purchase-orders.items.destroy', [event.value.id, purchaseOrder.value.id, item.id])).then(() => {
			showSuccess(trans('general.deleted'))
			const index:number = purchaseOrder.value.purchase_order_items.findIndex((i:PurchaseOrderItem) => item.id === i.id)
			if(index > -1){
				purchaseOrder.value.purchase_order_items.splice(index, 1)
			}
			purchaseOrder.value.price = 0
			purchaseOrder.value.purchase_order_items.forEach((i:PurchaseOrderItem) => {
				purchaseOrder.value.price += i.price
			})
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>